import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, X, Hash } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';

/**
 * Admin modal to configure PTW numbering templates per permit type.
 *
 * PTW reuses the shared document-numbering engine but in its own config
 * namespace, so each permit type can have an independent format + counter.
 *
 * Backend (routes/ptw.py):
 *   GET  /api/permit-number-config
 *   GET  /api/permit-number-config/preview?template=&permit_type=&seq_padding=
 *   PUT  /api/permit-number-config   { permit_type, template, seq_padding }
 */
const PERMIT_TYPES = [
  'Hot Work',
  'Cold Work',
  'Confined Space',
  'Work at Height',
  'Electrical Work',
  'Excavation',
  'Lifting Operations',
  'Chemical Handling',
  'Isolation Permit',
  'Radiography',
  'General Maintenance',
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Plain-language description + example for each token.
const TOKEN_INFO = {
  '{TYPE}': { label: 'Permit type code', example: 'HOT' },
  '{YYYY}': { label: 'Year (4 digits)', example: '2026' },
  '{YY}': { label: 'Year (2 digits)', example: '26' },
  '{MM}': { label: 'Month (2 digits)', example: '07' },
  '{SEQ}': { label: 'Running number (padded to the digits set below)', example: '0001' },
  '{SEQ:04d}': { label: 'Running number, fixed to 4 digits', example: '0001' },
  '{SITE}': { label: 'Site code', example: 'SITE1' },
};

const DEFAULT_TEMPLATE = 'PTW-{YYYY}-{SEQ:04d}';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Config key: '' (or the '__default__' sentinel used in the UI) maps to the
// PTW-wide default; a permit type name maps to a per-type override.
const DEFAULT_KEY = '__default__';

const PTWNumberingSettings = ({ open, onClose }) => {
  const { showSuccess, showError } = useAlert();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [canManage, setCanManage] = useState(false);

  // The permit type being edited; DEFAULT_KEY is the PTW-wide fallback.
  const [permitType, setPermitType] = useState(DEFAULT_KEY);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [seqPadding, setSeqPadding] = useState(4);
  const [preview, setPreview] = useState('');
  // Map of permit type (or DEFAULT_KEY) -> {template, seq_padding}.
  const [configs, setConfigs] = useState({});

  // Deliberately has no dependencies. The effect below re-runs this loader
  // whenever its identity changes, so depending on showError (or any other
  // context value) would refetch mid-edit and overwrite the user's unsaved
  // changes. showError is stable — AlertContext memoizes it.
  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/permit-number-config`, authHeaders());
      const prefix = res.data.prefix || 'PTW';
      const byType = {};
      (res.data.configs || []).forEach((c) => {
        // 'PTW' => default; 'PTW:<Type>' => that type.
        if (c.document_type === prefix) {
          byType[DEFAULT_KEY] = { template: c.template, seq_padding: c.seq_padding };
        } else if (c.document_type.startsWith(`${prefix}:`)) {
          byType[c.document_type.slice(prefix.length + 1)] = { template: c.template, seq_padding: c.seq_padding };
        }
      });
      setConfigs(byType);
      setTokens(res.data.tokens || []);
      setCanManage(!!res.data.can_manage);
      const def = byType[DEFAULT_KEY] || { template: DEFAULT_TEMPLATE, seq_padding: 4 };
      setPermitType(DEFAULT_KEY);
      setTemplate(def.template);
      setSeqPadding(def.seq_padding);
    } catch (e) {
      showError('Failed to load numbering configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadConfigs();
  }, [open, loadConfigs]);

  // When switching type, load its existing template (or fall back to default).
  const selectType = (type) => {
    setPermitType(type);
    const existing = configs[type] || configs[DEFAULT_KEY] || { template: DEFAULT_TEMPLATE, seq_padding: 4 };
    setTemplate(existing.template);
    setSeqPadding(existing.seq_padding);
  };

  // Debounced live preview from the server (authoritative renderer).
  useEffect(() => {
    if (!open || !template) { setPreview(''); return; }
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ template });
        if (permitType !== DEFAULT_KEY) params.append('permit_type', permitType);
        if (seqPadding) params.append('seq_padding', String(seqPadding));
        const res = await axios.get(`${API}/permit-number-config/preview?${params.toString()}`, authHeaders());
        setPreview(res.data.preview);
      } catch (e) {
        setPreview('(invalid template)');
      }
    }, 300);
    return () => clearTimeout(t);
  }, [template, permitType, seqPadding, open]);

  const trimmedTemplate = (template || '').trim();
  const hasSeqToken = /\{SEQ(:\d+d?)?\}/i.test(trimmedTemplate);
  const templateError = !trimmedTemplate
    ? 'Enter a number format.'
    : (!hasSeqToken ? 'Add a {SEQ} token so every permit gets its own number.' : '');

  const save = async () => {
    if (templateError) { showError(templateError); return; }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/permit-number-config`,
        { permit_type: permitType === DEFAULT_KEY ? '' : permitType, template, seq_padding: Number(seqPadding) },
        authHeaders()
      );
      setConfigs((prev) => ({ ...prev, [permitType]: { template: res.data.template, seq_padding: res.data.seq_padding } }));
      showSuccess('Numbering configuration saved.');
    } catch (e) {
      showError(e?.response?.data?.detail || 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Permit Numbering</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {!canManage && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                You can view these settings, but only tenant administrators can change them.
              </div>
            )}

            {/* Scope selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Applies to</label>
              <select
                value={permitType}
                onChange={(e) => selectType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value={DEFAULT_KEY}>Default (all permit types)</option>
                {PERMIT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}{configs[t] ? ' — customised' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Set a PTW-wide default, or override the format for a specific permit type.
                Each permit type numbers independently.
              </p>
            </div>

            {/* Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number format <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm font-mono ${templateError ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'}`}
                placeholder={DEFAULT_TEMPLATE}
              />
              {templateError && (
                <p className="text-xs text-red-600 mt-1">{templateError}</p>
              )}

              <p className="text-xs text-gray-600 mt-2 mb-1">Click a tag to add it to the format:</p>
              <div className="flex flex-wrap gap-1.5">
                {tokens.map((tok) => {
                  const info = TOKEN_INFO[tok];
                  return (
                    <button
                      key={tok}
                      type="button"
                      title={info ? `${info.label} — e.g. ${info.example}` : tok}
                      onClick={() => setTemplate((prev) => prev + tok)}
                      className="text-xs font-mono bg-slate-100 hover:bg-slate-200 text-slate-700 rounded px-2 py-0.5"
                    >
                      {tok}
                    </button>
                  );
                })}
              </div>

              <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-xs font-medium text-gray-600 mb-1.5">What each tag means</p>
                <ul className="space-y-0.5">
                  {tokens.filter((tok) => TOKEN_INFO[tok]).map((tok) => (
                    <li key={tok} className="text-xs text-gray-600 flex gap-2">
                      <code className="font-mono text-slate-700 min-w-[72px]">{tok}</code>
                      <span>{TOKEN_INFO[tok].label} <span className="text-gray-400">(e.g. {TOKEN_INFO[tok].example})</span></span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Tip: include <code className="font-mono">{'{SEQ}'}</code> for the running number. Add{' '}
                <code className="font-mono">{'{YYYY}'}</code> or <code className="font-mono">{'{YY}'}</code> to restart
                numbering each year, or <code className="font-mono">{'{MM}'}</code> to restart each month.
              </p>
            </div>

            {/* Padding */}
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">Sequence digits</label>
              <input
                type="number"
                min={1}
                max={12}
                value={seqPadding}
                onChange={(e) => setSeqPadding(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Digits the running number is padded to — e.g. 4 shows as <code className="font-mono">0001</code>.</p>
            </div>

            {/* Preview */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 mb-1">Next-number preview</div>
              <div className="text-lg font-mono font-bold text-slate-800">{preview || '—'}</div>
            </div>

          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={saving || loading || !!templateError || !canManage} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PTWNumberingSettings;
